// Deterministic 32-bit integer hashing (mechanics §5.1: pellet colours; RNG seeding; test
// fingerprints). Every function returns an unsigned 32-bit integer and gives the same result in
// every JS runtime, because it only uses Math.imul, shifts and xors.

const GOLDEN = 0x9e3779b9;

/**
 * Mixes a 32-bit integer into a well-distributed 32-bit hash (an xorshift-multiply finaliser).
 * The input is coerced with `>>> 0`, so pass integers (negative integers wrap to u32).
 * `hash32(0) === 0`; entity ids start at 1, so this never matters for pellet colours.
 */
export function hash32(x: number): number {
  let h = x >>> 0;
  h ^= h >>> 16;
  h = Math.imul(h, 0x21f0aaad);
  h ^= h >>> 15;
  h = Math.imul(h, 0x735a2d97);
  h ^= h >>> 15;
  return h >>> 0;
}

/** Folds the 32-bit integer `v` into the running hash `h`. The order of folding matters. */
export function hashCombine(h: number, v: number): number {
  return hash32((h ^ (Math.imul(v >>> 0, GOLDEN) + 0x7f4a7c15)) >>> 0);
}

/** Hashes a sequence of 32-bit integers (e.g. a determinism fingerprint, architecture §10.2). */
export function hashInts(values: Iterable<number>, seed = 0): number {
  let h = hash32(seed ^ GOLDEN);
  for (const v of values) h = hashCombine(h, v);
  return h;
}

/** Hashes a string by its UTF-16 code units (FNV-1a, then the `hash32` finaliser). */
export function hashString(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return hash32(h);
}
